import { FC, PropsWithChildren, useEffect } from 'react';

import { toast } from '../../atoms/Toast';
import createGenericContext from '../../helpers/createGenericContext';
import { useIsCypressRunning } from '../../hooks/useIsCypressRunning';
import { RUM_ACTION_NAME, trackRumAction } from '../../services/trackRumAction';

import { prepareNetworkStatusTraceContext } from './adapters/prepareNetworkStatusTraceContext';
import { useNetworkQuality } from './hooks/useNetworkQuality';
import messages from './messages';
import { NETWORK_TOAST_ID } from './NetworkStatusProvider.constants';
import { NETWORK_STATUS, NetworkStatus } from './NetworkStatusProvider.types';

export interface NetworkStatusContextValue {
  networkStatus: NetworkStatus;
}

const [useNetworkStatus, NetworkStatusContextProvider] =
  createGenericContext<NetworkStatusContextValue>();

const NetworkStatusProvider: FC<PropsWithChildren> = ({ children }) => {
  const isCypressRunning = useIsCypressRunning();
  const { networkStatus, networkContext, requestSamples, networkSamples } =
    useNetworkQuality({
      isEnabled: !isCypressRunning,
    });

  useEffect(() => {
    return () => {
      toast.dismiss(NETWORK_TOAST_ID);
    };
  }, []);

  useEffect(() => {
    toast.dismiss(NETWORK_TOAST_ID);

    if (networkStatus === NETWORK_STATUS.GOOD) {
      return;
    }

    trackRumAction(RUM_ACTION_NAME.NETWORK_STATUS_CHANGED, {
      ...prepareNetworkStatusTraceContext({
        networkStatus,
        networkContext,
        requestSamples,
        networkSamples,
      }),
    });

    if (networkStatus === NETWORK_STATUS.OFFLINE) {
      toast.error(messages.offlineBody, {
        autoClose: false,
        toastId: NETWORK_TOAST_ID,
        title: messages.offlineTitle,
      });

      return;
    }

    toast.warn(messages.degradedBody, {
      autoClose: false,
      toastId: NETWORK_TOAST_ID,
      title: messages.degradedTitle,
    });
  }, [networkStatus, networkContext, requestSamples, networkSamples]);

  return (
    <NetworkStatusContextProvider value={{ networkStatus }}>
      {children}
    </NetworkStatusContextProvider>
  );
};

export { NetworkStatusProvider, useNetworkStatus };
